// The choice step's option rows: key, label, an icon picked from the ten
// built-in shapes (inline swatches — see choice-icons.ts, mirrored from the
// widget's own icon module), an optional image, and, for the scored step,
// the numeric value the option scores. Row count stays inside
// surveys-core's 2..=10 bound (survey-schema.ts enforces the same bound
// before the PUT); the add/remove controls below just keep the table from
// going outside it in the first place.

import { Button, Input, NumberStepper } from "@pej/kore/components";
import { Image as ImageIcon, Plus, X } from "lucide-react";
import { useId, useState } from "react";
import type { Control, FieldArrayWithId, UseFieldArrayAppend, UseFieldArrayRemove } from "react-hook-form";
import { Controller, useController } from "react-hook-form";
// Generic asset-image slot (value/onChange/pickerTitle/unavailableLabel) —
// built for the template editor's two named slots, but nothing about it is
// template-specific, so it is reused here for each option's image rather
// than re-built against features/assets' lower-level asset-field.
import { ArtworkSlot } from "@/features/templates/artwork-field";
import { cn } from "@/lib/utils";
import { BUILTIN_ICON_NAMES, builtinIconSvg } from "./choice-icons";
import { OPTION_CAPTION } from "./option-caption";
import { MAX_OPTIONS, type StepsFormValues } from "./survey-schema";

const MIN_OPTIONS = 2;

export interface ChoiceOptionsEditorProps {
    control: Control<StepsFormValues>;
    stepIndex: number;
    /** Whether this step is the survey's score — gates the score stepper. */
    scored: boolean;
    // The field array lives in the parent (steps/step-section.tsx): switching a
    // preset needs to swap the whole array via useFieldArray's own
    // `replace`, not `form.setValue` — setValue changes the form's values
    // but does not reliably resync a field array's own tracked `fields`
    // (and the stable per-row keys they carry), a documented RHF gotcha. A
    // single `useFieldArray` call shared this way is what keeps the preset
    // switch and this editor's own add/remove button working off the one
    // source of truth.
    fields: FieldArrayWithId<StepsFormValues, `steps.${number}.options`, "id">[];
    append: UseFieldArrayAppend<StepsFormValues, `steps.${number}.options`>;
    remove: UseFieldArrayRemove;
}

export function ChoiceOptionsEditor({ control, stepIndex, scored, fields, append, remove }: ChoiceOptionsEditorProps) {
    function addOption() {
        const position = fields.length;
        append({ key: `option-${position + 1}`, label: "", icon: "", assetId: "", assetUrl: "", value: position + 1 });
    }

    return (
        <div className="flex flex-col gap-3">
            {fields.map((field, index) => (
                <OptionRow
                    key={field.id}
                    control={control}
                    stepIndex={stepIndex}
                    index={index}
                    scored={scored}
                    canRemove={fields.length > MIN_OPTIONS}
                    onRemove={() => remove(index)}
                />
            ))}
            <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addOption}
                disabled={fields.length >= MAX_OPTIONS}
            >
                <Plus aria-hidden="true" />
                Add option
            </Button>
        </div>
    );
}

interface OptionRowProps {
    control: Control<StepsFormValues>;
    stepIndex: number;
    index: number;
    scored: boolean;
    canRemove: boolean;
    onRemove: () => void;
}

function OptionRow({ control, stepIndex, index, scored, canRemove, onRemove }: OptionRowProps) {
    const label = `Step ${stepIndex + 1} option ${index + 1}`;
    const path = `steps.${stepIndex}.options.${index}` as const;
    const id = useId();
    return (
        // What the option IS (key, label, score) above what it LOOKS LIKE:
        // an icon or an image, never both. The image slot gets its own row
        // because the asset field needs more width than is left beside the
        // swatches. Remove sits in the
        // card's corner, on no row, in a column the card's right padding
        // reserves for it so every row ends at the same edge — when the
        // layout wrapped it landed beside the score stepper and read as
        // "remove the score".
        <div className="relative flex flex-col gap-3 rounded-kore-md border border-kore-border p-3 pr-12">
            <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Remove ${label.toLowerCase()}`}
                onClick={onRemove}
                disabled={!canRemove}
                className="absolute right-1.5 top-1.5"
            >
                <X aria-hidden="true" />
            </Button>
            <div className="flex items-end gap-3">
                <div className="flex w-24 flex-col gap-1.5">
                    <label htmlFor={`${id}-key`} className={OPTION_CAPTION}>
                        Key
                    </label>
                    <Controller
                        control={control}
                        name={`${path}.key`}
                        render={({ field }) => (
                            <Input {...field} id={`${id}-key`} aria-label={`${label} key`} placeholder="key" />
                        )}
                    />
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                    <label htmlFor={`${id}-label`} className={OPTION_CAPTION}>
                        Label
                    </label>
                    <Controller
                        control={control}
                        name={`${path}.label`}
                        render={({ field }) => (
                            <Input {...field} id={`${id}-label`} aria-label={`${label} label`} placeholder="Label" />
                        )}
                    />
                </div>
                {scored && (
                    <div className="flex flex-col gap-1.5">
                        {/* The stepper names itself; this is the visible caption. */}
                        <span aria-hidden="true" className={OPTION_CAPTION}>
                            Score
                        </span>
                        <Controller
                            control={control}
                            name={`${path}.value`}
                            render={({ field: { value, onChange } }) => (
                                <NumberStepper
                                    value={value}
                                    onChange={(next) => onChange(next === "" ? 0 : next)}
                                    ariaLabel={`${label} score`}
                                />
                            )}
                        />
                    </div>
                )}
            </div>
            <OptionLook control={control} path={path} label={label} />
        </div>
    );
}

interface OptionLookProps {
    control: Control<StepsFormValues>;
    path: `steps.${number}.options.${number}`;
    label: string;
}

/** Icon or image, never both. Picking an image (not just the tile) clears
 *  the icon, so a cancelled pick loses nothing. */
function OptionLook({ control, path, label }: OptionLookProps) {
    const icon = useController({ control, name: `${path}.icon` });
    const asset = useController({ control, name: `${path}.assetId` });
    // Image chosen but nothing picked yet: the slot stays open on its own.
    const [imageOpen, setImageOpen] = useState(false);
    const imageChosen = imageOpen || asset.field.value !== "";
    return (
        <div className="flex flex-col gap-1.5">
            {/* The swatch group names itself; this is the visible caption. */}
            <span aria-hidden="true" className={OPTION_CAPTION}>
                Icon or image
            </span>
            <IconSwatchPicker
                value={imageChosen ? null : icon.field.value}
                onChange={(name) => {
                    icon.field.onChange(name);
                    asset.field.onChange("");
                    setImageOpen(false);
                }}
                imageChosen={imageChosen}
                onChooseImage={() => setImageOpen(true)}
                labelPrefix={label}
            />
            {imageChosen && (
                // Capped so the dropzone doesn't stretch across a wide card.
                <div className="mt-1.5 w-full max-w-sm">
                    <ArtworkSlot
                        value={asset.field.value}
                        onChange={(next) => {
                            asset.field.onChange(next);
                            if (next) icon.field.onChange("");
                        }}
                        pickerTitle="Choose an image"
                    />
                </div>
            )}
        </div>
    );
}

interface IconSwatchPickerProps {
    /** The chosen icon, "" for None, null while Image is chosen. */
    value: string | null;
    onChange: (next: string) => void;
    imageChosen: boolean;
    onChooseImage: () => void;
    labelPrefix: string;
}

/** Inline swatch grid over the ten built-in shapes, a "None" tile and an
 *  Image tile — a picker, not a Select: every choice is visible at once,
 *  which is what makes it worth calling a picker rather than a dropdown. */
function IconSwatchPicker({ value, onChange, imageChosen, onChooseImage, labelPrefix }: IconSwatchPickerProps) {
    const swatchClass = (selected: boolean) =>
        cn(
            "flex size-8 shrink-0 items-center justify-center rounded-kore-sm border p-1.5 text-kore-text-muted transition-colors",
            selected ? "border-kore-accent bg-kore-accent-softer text-kore-accent-text" : "border-kore-border",
        );

    return (
        // biome-ignore lint/a11y/useSemanticElements: <fieldset> is for grouping form controls and carries browser-default border/margin chrome this swatch grid can't use; this groups plain buttons, not form inputs, so div+role="group" is the correct pattern (see kore's components/assets/folder-chip for the same call).
        <div role="group" aria-label={`${labelPrefix} icon or image`} className="flex min-w-0 flex-1 flex-wrap gap-1">
            <button
                type="button"
                aria-label={`${labelPrefix} icon: none`}
                aria-pressed={value === ""}
                onClick={() => onChange("")}
                className={cn(swatchClass(value === ""), "text-[10px]")}
            >
                None
            </button>
            {BUILTIN_ICON_NAMES.map((name) => (
                <button
                    key={name}
                    type="button"
                    aria-label={`${labelPrefix} icon: ${name}`}
                    aria-pressed={value === name}
                    onClick={() => onChange(name)}
                    className={swatchClass(value === name)}
                    // biome-ignore lint/security/noDangerouslySetInnerHtml: static, module-authored SVG shapes (choice-icons.ts) — no user input reaches this markup.
                    dangerouslySetInnerHTML={{ __html: builtinIconSvg(name) ?? "" }}
                />
            ))}
            <button
                type="button"
                aria-label={`${labelPrefix} image`}
                aria-pressed={imageChosen}
                onClick={onChooseImage}
                className={swatchClass(imageChosen)}
            >
                <ImageIcon aria-hidden="true" className="size-4" />
            </button>
        </div>
    );
}
